import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom"
import { useTenantNavigation } from "../../../hooks/useTenantNavigation.js"
import { Typography, Flex, Button, Table, Modal, Form, message, Input, InputNumber, Dropdown, Popconfirm, Select } from "antd";
import TextArea from "antd/es/input/TextArea"
import ParentSelector from "./ParentSelector";
import BreadCrumb from "../../BreadCrumb";
import { useSelector } from "react-redux";
import { EditOutlined, HomeOutlined, MoreOutlined, PlusOutlined, SearchOutlined, UploadOutlined, QuestionCircleOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getDataIndexFromLabel, getLabel } from "../../../utils/getNameFromEmail";
import AddDataModal from "./AddDataModal";
import { createDatabase, getSingleMasterData, getMasterList, saveRateMaster, getRateMaster, downloadTemplate, uploadTemplate, deleteMasterData, deleteMasterDataRecord } from "../../../services/adminServices";
import { showLoader } from "../../../utils/showloader";
import { extractOptionsForDataAdd } from "../../../utils/extractOptionsForDataAdd";
import { trimStringValues } from "../../../utils/trimStrings";
import { convertKeyToLabel } from "../../../utils/formsHelper";
import { isValidDate, valueModifier } from "../../../utils/requestHelper.js";
import { buildGlobalMasterLabelMap, buildMasterOptionLabelMap, isUuidLike, resolveMasterValueLabel } from "../../../utils/masterHelper";
import { getFormattedDate } from "../../../utils/getLocalFormattedDate.js";
import { FORM_LANGUAGE_OPTIONS } from "../../../utils/formTranslations.js";
import {
    DEFAULT_LANGUAGE,
    buildRowTranslationDraft,
    countTranslatedRows,
    hasTranslatableColumns,
    mergeRowTranslations,
    resolveMasterFieldsTranslation,
    resolveMasterNameTranslation,
    resolveRowTranslation,
} from "../../../utils/masterTranslations.js";

const fixedField = ['Employee Level', 'Skill Level1', 'Skill Level2', 'Team', 'Location', 'Country', 'Region']

const formatMasterCellValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return value
    }

    if (Array.isArray(value)) {
        return value
            .filter(item => item !== undefined && item !== null && item !== "")
            .map(item => {
                if (typeof item === "object") {
                    return item?.label || item?.name || item?.value || ""
                }
                return item
            })
            .filter(Boolean)
            .join(", ")
    }

    if (typeof value === "object") {
        if (Array.isArray(value.user)) {
            return value.user.join(", ")
        }
        if (typeof value.label === "string") {
            return value.label
        }
        if (typeof value.name === "string") {
            return value.name
        }
    }

    if(isValidDate(value)){
        return getFormattedDate(value)
    }

    return value
}

const DataTables = () => {
    const { t, i18n } = useTranslation();
    const navigate = useTenantNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const table = searchParams.get("table")
    const { teamsData } = useSelector(state => state.masterData)
    const [masterData, setMasterData] = useState({})
    const [columns, setColumns] = useState([])
    const [columnFields, setColumnFields] = useState([])
    const [originalFields, setOriginalFields] = useState([])
    const [formFields, setFormFields] = useState([])
    const [tableData, setTableData] = useState([])
    const [masterList, setMasterList] = useState([])
    const [parentMasterData, setParentMasterData] = useState([])
    const [openDataForm, setOpenDataForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false)
    const [selectedData, setSelectedData] = useState(null)
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const { tableId } = useParams()
    const isLoadingRef = useRef(false)
    const fullDefinitionFetchRef = useRef(false)
    const [searchVisible, setSearchVisible] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [filteredData, setFilteredData] = useState([])
    // The grid always reads in the user's own language (their saved
    // `defaultLanguage`, which drives the app locale) — there is nothing to choose.
    const contentLang = i18n.language || DEFAULT_LANGUAGE
    // Edits in a non-default language write into `translations[lng]` only, so the
    // canonical record — the one every stored request points at — is preserved
    const [selectedRawRecord, setSelectedRawRecord] = useState(null)
    // Authoring a translation is the one place a language is picked explicitly, so
    // the record modal carries its own selector and opens on the reader's language.
    const [modalLang, setModalLang] = useState(DEFAULT_LANGUAGE)
    const isTranslationMode = contentLang !== DEFAULT_LANGUAGE
    const isModalTranslating = modalLang !== DEFAULT_LANGUAGE

    // Column headers, option labels and the table name in the reader's language.
    // Data keys stay canonical — only the text shown to the reader changes.
    const localizedFields = useMemo(
        () => resolveMasterFieldsTranslation(columnFields, contentLang),
        [columnFields, contentLang]
    )
    const displayTableName = resolveMasterNameTranslation(masterData, contentLang) || getLabel(table)
    const translationSummary = useMemo(() => {
        if (!isTranslationMode || !hasTranslatableColumns(columnFields) || tableData.length === 0) {
            return ""
        }
        return `${countTranslatedRows(tableData, columnFields, contentLang)}/${tableData.length} ${t("translated")}`
    }, [isTranslationMode, columnFields, tableData, contentLang, t])

    const breadCrumbItems = [
        {
            title: <HomeOutlined />,
            path: "/app/dashboard",
        },
        {
            title: t("Admin"),
            path: "/app/admin/config"
        },
        {
            title: displayTableName,
        },
    ]
    const getData = async () => {
        showLoader(true)
        let response = await getMasterList();
        showLoader(false)
        if (response.data) {
            setMasterList(response.data)
        } else {
            setMasterList([])
        }
    }



    useEffect(() => {
        getData();
    }, [])

    const fetchMasterData = async () => {
        let requireMasterData = await getRateMaster();
        if (requireMasterData.data) {
            let reqData = convertKeyToLabel(requireMasterData.data)
            setTableData(reqData)
        }
    }

    // Loads a record into the modal for `lng`. Translating shows only this
    // language's text — blank where untranslated — so the canonical values are
    // never sitting in an editable control waiting to be overwritten.
    const loadRecordIntoForm = (rawRecord, transformedRecord, fields, lng) => {
        if (!form || !form.setFieldsValue) return
        if (lng !== DEFAULT_LANGUAGE) {
            form.setFieldsValue(buildRowTranslationDraft(rawRecord, fields, lng))
            return
        }
        form.setFieldsValue({ ...valueModifier(transformedRecord) })
    }

    const handleEditClick = (record, formFields) => {
        let selectedRecord = { ...record };

        // Transform data for userForm fields
        Object.keys(selectedRecord).forEach(key => {
            let currentField = formFields.find(field => field.label === key)
            if (currentField?.formType === "userForm") {
                // If it's already in the correct format, don't transform
                if (typeof selectedRecord[key] === 'string') {
                    selectedRecord[key] = { "user": [selectedRecord[key]] }
                }
            }
        })

        // Set states first
        setIsEdit(true)
        setSelectedData(selectedRecord)
        setSelectedRawRecord(record)
        setModalLang(contentLang)
        setOpenDataForm(true)

        // Set form values after modal opens
        setTimeout(() => {
            try {
                loadRecordIntoForm(record, selectedRecord, formFields, contentLang)
            } catch (error) {
            }
        }, 300)
    }

    // Switching language inside the modal swaps which text is being edited. The
    // previous language's values are already saved or discarded with Cancel, so
    // the form is rebuilt from the record rather than carried across.
    const handleModalLanguageChange = (lng) => {
        setModalLang(lng)
        if (!selectedRawRecord) return
        form.resetFields()
        // Let the fields for the new language mount before filling them
        setTimeout(() => {
            try {
                loadRecordIntoForm(selectedRawRecord, selectedData, formFields, lng)
            } catch (error) {
                console.error("Error switching translation language:", error)
            }
        }, 0)
    }

    const handleDelete = async (record) => {
        // API call to delete data
        showLoader(true)
        let response = await deleteMasterDataRecord(tableId, record.uuid);
        showLoader(false)
        if (response.status === 200) {
            message.success(t('Data deleted successfully'));
            getTableData();
        } else {
            message.error(response.message || "Failed to delete data");
        }
    }

    const confirmSpaceDelete = (record) => {
        Modal.confirm({
          title: t('Confirm'),
          content: t('Are you sure to delete the record?'),
          okText: t("Yes"),
          cancelText: t("No"),
          onOk: (event) => {
            handleDelete(record)
            Modal.destroyAll()
          },
          footer: (_, { OkBtn, CancelBtn }) => (
            <>
              <CancelBtn />
              <OkBtn />
            </>
          ),
        });
      }

    // The list endpoint may return tables without their field definitions, so referenced
    // tables are fetched in full and merged in before option labels are resolved
    const combinedMasterList = useMemo(() => {
        let combined = Array.isArray(masterList) ? [...masterList] : []
        parentMasterData.forEach(master => {
            let existingIndex = combined.findIndex(item => item?.dataId === master?.dataId)
            if (existingIndex >= 0) {
                combined[existingIndex] = { ...combined[existingIndex], ...master }
            } else {
                combined.push(master)
            }
        })
        return combined
    }, [masterList, parentMasterData])

    // Option ids are turned into labels on the rows themselves, the same way the form
    // dropdown resolves its option list, so the table never has to map them while rendering
    const resolvedTableData = useMemo(() => {
        if (tableData.length === 0 || columnFields.length === 0) {
            return tableData
        }

        const globalLabelMap = buildGlobalMasterLabelMap(combinedMasterList, contentLang)
        const fieldLabelMaps = new Map()
        const getFieldLabelMap = (field) => {
            if (!fieldLabelMaps.has(field)) {
                fieldLabelMaps.set(field, buildMasterOptionLabelMap(field, combinedMasterList, contentLang))
            }
            return fieldLabelMaps.get(field)
        }

        return tableData.map(row => {
            // Text columns carry their translation on the row itself; id columns are
            // relabelled by the maps below, which resolve in the same language
            const localizedRow = resolveRowTranslation(row, columnFields, contentLang)
            let resolvedRow = { ...localizedRow }
            columnFields.forEach(field => {
                if (Array.isArray(field?.children)) {
                    const groupValue = localizedRow?.[field.label]
                    if (groupValue && typeof groupValue === "object") {
                        let resolvedGroup = { ...groupValue }
                        field.children.forEach(child => {
                            resolvedGroup[child.label] = resolveMasterValueLabel(groupValue[child.label], getFieldLabelMap(child), globalLabelMap)
                        })
                        resolvedRow[field.label] = resolvedGroup
                    }
                    return
                }
                resolvedRow[field.label] = resolveMasterValueLabel(localizedRow?.[field.label], getFieldLabelMap(field), globalLabelMap)
            })
            // Edit and delete must act on the stored ids, not on the display labels
            resolvedRow.__rawRecord = row
            return resolvedRow
        })
    }, [tableData, columnFields, combinedMasterList, contentLang])

    // Titles come from `localised` while every dataIndex stays on the canonical
    // label, which is the key the row is actually stored under
    const buildColumns = (fields, localised = []) => {
        let cols = fields.map((field, index) => {
            const localisedField = localised[index] || field
            if (field.hasOwnProperty("children")) {
                let fieldChildren = field.children.map((child, childIndex) => {
                    return {
                        title: localisedField?.children?.[childIndex]?.label || child.label,
                        dataIndex: child.label,
                        render: (text, rec) => formatMasterCellValue(rec?.[field.label]?.[child.label])
                    }
                })
                return { title: localisedField.label, children: fieldChildren }
            }
            let reqField = { title: localisedField.label, dataIndex: field.label }
            reqField.render = (text) => formatMasterCellValue(text)
            if (fixedField.includes(field.label)) {
                reqField['fixed'] = 'left'
                // reqField['width'] = 120
            }
            return { ...reqField }
        })
        cols.push({
            title: t('Action'),
            dataIndex: "",
            fixed: 'right',
            render: (text, record) => {
                return <Flex gap={16}>
                    <EditOutlined className="cursor-pointer" onClick={() => handleEditClick(record.__rawRecord || record, fields)} />
                    <DeleteOutlined onClick={() => confirmSpaceDelete(record.__rawRecord || record)} />
                </Flex>
            }
        })
        return cols
    }

    const getColumnsAndData = async (mainData) => {
        if (mainData.fields) {
            let formFieldsWithOptions = extractOptionsForDataAdd(mainData.fields, masterList)
            setColumnFields([...mainData.fields])
            setFormFields([...mainData.fields])
            setOriginalFields([...mainData.fields])
        } else {
            setColumnFields([])
            setColumns([])
            setFormFields([])
            setOriginalFields([])
        }
        if (table?.toLowerCase() === "rate master") {
            await fetchMasterData();
        } else {
            if (mainData.data) {
                let formattedData = mainData.data
                formattedData = formattedData.map(data => {
                    Object.keys(data).forEach(key => {
                        if (typeof data[key] === "object" && data[key].hasOwnProperty("user")) {
                            data[key] = data[key]?.["user"]?.[0]
                        }

                    })
                    return { ...data }
                })
                setTableData(mainData.data)

            } else {
                setTableData([])
            }
        }
    }

    const getTableData = async (fetchData=false) => {
        if (isLoadingRef.current || !tableId || tableId === 'undefined') return; // Prevent duplicate calls and check tableId exists

        isLoadingRef.current = true;
        showLoader(true)
        let response = await getSingleMasterData(tableId)
        showLoader(false)
        isLoadingRef.current = false;

        if (response.data) {
            setMasterData(response.data)
            if (columns.length === 0 || fetchData)
                await getColumnsAndData(response.data)

        } else {
            setMasterData()
            message.error(response.message)
        }
    }

    useEffect(() => {
        if (tableId && tableId !== 'undefined' && !isLoadingRef.current) {
            getTableData();
        }
    }, [tableId]) // Simplified dependency array

    useEffect(() => {
        const collectParentIds = (fields) => {
            let ids = []
            if (!Array.isArray(fields)) {
                return ids
            }
            fields.forEach(field => {
                if (field?.parentTable) {
                    ids.push(field.parentTable)
                }
                ids = ids.concat(collectParentIds(field?.children))
            })
            return ids
        }

        let parentIds = [...new Set(collectParentIds(columnFields))]
            .filter(id => id && id !== tableId && !parentMasterData.some(master => master?.dataId === id))
        if (parentIds.length === 0) {
            return
        }

        let cancelled = false
        Promise.all(parentIds.map(id => getSingleMasterData(id))).then(responses => {
            if (cancelled) {
                return
            }
            let fetchedTables = responses.map(response => response?.data).filter(data => Array.isArray(data?.fields))
            if (fetchedTables.length > 0) {
                setParentMasterData(prev => [...prev, ...fetchedTables])
            }
        })

        return () => { cancelled = true }
    }, [columnFields, tableId])

    // A field can hold an option id of another table without declaring which table it came from,
    // and the list endpoint does not always carry field definitions. When an id cannot be
    // resolved from what is loaded, pull every remaining table definition in once.
    useEffect(() => {
        if (fullDefinitionFetchRef.current || tableData.length === 0 || masterList.length === 0) {
            return
        }

        const globalLabelMap = buildGlobalMasterLabelMap(combinedMasterList)
        const hasUnresolvedId = tableData.some(row => Object.entries(row || {}).some(([key, value]) =>
            key !== "uuid" && key !== "_id" && key !== "dataId" && isUuidLike(value) && !globalLabelMap.has(value)
        ))
        if (!hasUnresolvedId) {
            return
        }

        // The list entries do not reliably expose dataId, so fall back to fetching by name
        let pendingTables = masterList
            .map(master => ({
                dataId: master?.dataId || master?._id || master?.id || "",
                dataName: master?.dataName || ""
            }))
            .filter(master => (master.dataId || master.dataName)
                && master.dataId !== tableId
                && !parentMasterData.some(loaded => (loaded?.dataId && loaded.dataId === master.dataId) || (loaded?.dataName && loaded.dataName === master.dataName)))
        if (pendingTables.length === 0) {
            return
        }

        fullDefinitionFetchRef.current = true
        let cancelled = false
        Promise.all(pendingTables.map(master => getSingleMasterData(master.dataId, master.dataId ? undefined : master.dataName))).then(responses => {
            if (cancelled) {
                return
            }
            let fetchedTables = responses.map(response => response?.data).filter(data => Array.isArray(data?.fields))
            if (fetchedTables.length > 0) {
                setParentMasterData(prev => [...prev, ...fetchedTables])
            }
        })

        return () => { cancelled = true }
    }, [tableData, masterList, combinedMasterList, tableId])

    useEffect(() => {
        fullDefinitionFetchRef.current = false
    }, [tableId])

    useEffect(() => {
        if (columnFields.length > 0) {
            setColumns(buildColumns(columnFields, localizedFields))
        }
    }, [columnFields, localizedFields])

    useEffect(() => {
        if (teamsData.columns.length > 0) {
            let formattedColumns = teamsData.columns.map(col => {
                return { title: col.label, dataIndex: getDataIndexFromLabel(col.label) }
            })
            setColumns(formattedColumns)
        }
    }, [teamsData])

    useEffect(() => {
        if (table === "Rate Master" && formValues) {

        }
    }, [formValues, table])

    useEffect(() => {
        setFilteredData(resolvedTableData)
        setSearchText("")
    }, [resolvedTableData])

    const gotoForm = () => {
        navigate("/app/admin/create-master/form-editor/" + tableId + "?table=" + table)
    }


    const saveData = async (values) => {
        values = trimStringValues(values);

        // Translating never rewrites the record: the canonical row is sent back
        // untouched with the language bucket merged onto it, so requests that
        // point at this row's uuid keep resolving exactly as before.
        if (isModalTranslating) {
            if (!selectedRawRecord) {
                message.error(t("Select a record to translate"))
                return
            }
            let translatedRow = mergeRowTranslations(selectedRawRecord, formFields, modalLang, values)
            let translationPostData = {
                dataId: masterData.dataId,
                dataName: masterData.dataName,
                fields: masterData.fields,
                data: [translatedRow],
            }
            showLoader(true)
            let translationResponse = await createDatabase(translationPostData, true)
            showLoader(false)
            if (translationResponse.status === 200) {
                message.success(t("Translation saved"))
                setMasterData(translationResponse.data)
                getColumnsAndData(translationResponse.data)
                closeDataFormModal()
            } else {
                message.error(translationResponse.message)
            }
            return
        }

        let dataValues = { ...selectedData, ...values };
        Object.keys(dataValues).forEach(key => {
            let currentField = formFields.find(field => field.label === key)
            if (currentField?.formType === "userForm") {
                dataValues[key] = dataValues[key]?.["user"]?.[0]
            }
        })
        // return

        let postData = { dataId: masterData.dataId, dataName: masterData.dataName, fields: masterData.fields, data: [{ ...dataValues }] }

        if (postData.dataName.toLowerCase() === "rate master") {
            let reqValues = { ...values }
            if (isEdit) {
                reqValues = {}
                Object.keys(values).forEach(key => {
                    if ((key.includes("Onsite") || key.includes("Offshore")) && values[key]) {
                        reqValues[key] = values[key]
                    }
                })
                reqValues.dataId = selectedData["Data Id"]
            } else {
                Object.keys(values).forEach(key => {
                    postData[key] = values[key] || ""
                })
            }

            postData = { ...reqValues, "Employee Level": String(values["Employee Level"]) }

            // postData =  {...postData, "Off Shore": offShore, "On Site": onSite}
            // setTableData([postData])
            // return
            showLoader(true)
            let updating = postData["dataId"] ? true : false
            let updateResponse = await saveRateMaster(postData, updating)
            showLoader(false)
            if (updateResponse.status === 200) {
                message.success(updateResponse.message)
                closeDataFormModal()
                setIsEdit(false)
                setSelectedData(null)
                await fetchMasterData()
                return
            }
            return

        }
        // return
        showLoader(true)
        let updateResponse = await createDatabase(postData, true)
        showLoader(false)
        if (updateResponse.status === 200) {
            setMasterData(updateResponse.data)
            getColumnsAndData(updateResponse.data)
        }
        else {
            message.error(updateResponse.message)
        }
        setOpenDataForm(false)
        form.resetFields();
    }

    const closeDataFormModal = () => {
        setOpenDataForm(false)
        setIsEdit(false)
        setSelectedData(null)
        setSelectedRawRecord(null)
        setModalLang(DEFAULT_LANGUAGE)
        setFormFields([...originalFields])

        // Reset form after a small delay to ensure modal is closed
        setTimeout(() => {
            form.resetFields();
        }, 100)
    }

    const formChangeHandler = (key, value, index) => {
        let updatedTable = tableData.map((data, rowIndex) => {
            if (index === rowIndex) {
                return { ...data, [key]: value }
            }
            return { ...data }
        })
        // Don't set form values here as it can cause infinite loops
        // form.setFieldsValue({ ...formValues, [key]: value })
        setTableData(updatedTable)
    }

    const getElement = (item, parentLabel = "", index) => {
        let itemName = item.label;
        let itemLabel = getLabel(item.label)
        const disabled = item.readOnly ? true : false
        const help = item.tooltip ? { title: item.helpText, icon: <QuestionCircleOutlined /> }
            : undefined;
        let rules = item.mandatory ? [{ required: true, whitespace: item.formType !== "dropdownField", message: item.label + " is required" }] : undefined

        if (item.formType === "inputForm" || item.formType === "numberForm") {
            return <Form.Item key={item.label} label="" name={parentLabel ? [parentLabel, itemName] : itemName} tooltip={help} rules={rules}>
                <Input placeholder={item.placeholder} disabled={disabled} onChange={(e) => formChangeHandler(item.label, e.target.value, index)} />
            </Form.Item>
        } else if (item.formType === "dropdownField") {
            return <ParentSelector tooltip={help} rules={rules} originalLabel={item.label} name={itemName} {...item} label="" disabled={disabled} masterList={masterList} index={index} changeHandler={formChangeHandler} formValues={formValues} masterData={masterData} />

        } else if (item.formType === "numberForm") {
            return <Form.Item key={item.label} label="" name={itemName} tooltip={help} rules={rules}>
                <InputNumber placeholder={item.placeholder} disabled={disabled} style={{ width: "100%" }} />
            </Form.Item>
        } else {
            <Form.Item key={item.label} label="" name={itemName} tooltip={help} rules={rules}>
                <TextArea placeholder={item.placeholder} rows={item.maxHeight || 2} disabled={disabled} />
            </Form.Item>
        }
    }

    const handleNewDataAddition = () => {
        if (formFields.length === 0) {
            message.error(t('Please define a form to continue'));
            return
        }
        // Reset form and states for new entry
        setIsEdit(false)
        setSelectedData(null)
        setSelectedRawRecord(null)
        // A record is always created in the default language and translated
        // afterwards, so its canonical values exist for every language to fall back to
        setModalLang(DEFAULT_LANGUAGE)
        form.resetFields()
        setOpenDataForm(true)
    }

    const handleItemChange = (label, val, parentLabel) => {
        let updatedItems = [...formFields]
        updatedItems = updatedItems.map(field => {
            let updatedField = { ...field }
            if (updatedField.label === parentLabel) {
                let disabled = val ? true : false
                updatedField.children = updatedField.children.map(child => {
                    if (child.label !== label) {
                        return { ...child, readOnly: disabled }
                    }
                    return { ...child, readOnly: false }
                })
            }
            return { ...updatedField }
        })
        setFormFields(updatedItems)
    }

    const handleSearch = (value) => {
        setSearchText(value)
        if (!value) {
            setFilteredData(resolvedTableData)
            return
        }

        const searchLower = value.toLowerCase()
        const matchesValue = (val) => {
            if (val && typeof val === 'object') {
                return Object.values(val).some(nestedVal =>
                    String(nestedVal).toLowerCase().includes(searchLower)
                )
            }
            return String(val).toLowerCase().includes(searchLower)
        }
        const searchableValues = (record) =>
            Object.entries(record || {})
                .filter(([key]) => key !== "__rawRecord" && key !== "translations")
                .map(([, val]) => val)

        // A record stays findable by its canonical text even while the grid shows
        // another language, so an English search still locates a translated row
        const filtered = resolvedTableData.filter(record =>
            searchableValues(record).some(matchesValue) ||
            searchableValues(record.__rawRecord).some(matchesValue)
        )
        setFilteredData(filtered)
    }

    const toggleSearch = () => {
        if (searchVisible) {
            setSearchText("")
            setFilteredData(resolvedTableData)
        }
        setSearchVisible(!searchVisible)
    }

    const locale = {
        emptyText: (
            <Flex vertical align="center" justify="center">
                <img src="/assets/MasterData.svg" alt="master-data-table-icon" />
                <p style={{ fontSize: "1rem", fontWeight: "600", color: "#000", marginTop: "0" }}>{t('Start by creating new entry in master data')}</p>
                <Button size="large" className="submit-button my-2" icon={<PlusOutlined />} onClick={handleNewDataAddition}>{t('New')}</Button>
            </Flex>
        ),
    };

    const downloadMasterTemplate = async () => {
        showLoader(true)
        let downloadResponse = await downloadTemplate(masterData.dataId)
        showLoader(false)
        if (downloadResponse.status === 200) {
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', masterData.dataName + ".csv"); // set desired file name here
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
    }

    const customRequest = async (event) => {
        // event.preventDefault()
        let selectedFile;

        if (event.target.files && event.target.files.length > 0) {
            selectedFile = event.target.files[0]; // Store the selected file
        }
        if (!selectedFile.type.includes("csv")) {
            message.error(t('Please upload csv'))
            return null;
        }
        const data = new FormData()
        const myNewFile = new File([selectedFile], selectedFile.name, { type: selectedFile.type });
        data.append('file', myNewFile);

        let uploadResponse = await uploadTemplate(masterData.dataId, data)
        if (uploadResponse.status === 200) {
            message.success(t('Data uploaded successfully'))
            await getTableData(true);
        }
    }

    const databaseActions = [
        {
            key: "copy",
            label: (
                <Flex gap={8} className="w-28">
                    <DownloadOutlined className="text-blue-500" />
                    <span className="text-gray-700">{t('Download Template')}</span>
                </Flex>
            ),
            onClick: downloadMasterTemplate
        }
    ]

    return (
        <div className="px-10 py-4">
            <div style={{ marginTop: "24px" }}>
                <BreadCrumb
                    items={breadCrumbItems}
                    marginb="16px"
                />
            </div>
            <Flex align="center" justify="space-between">
                <Flex vertical>
                    <Typography.Title level={4} style={{ margin: 0, padding: 0 }}>{displayTableName}</Typography.Title>
                    {translationSummary && <span className="text-color-secondary" style={{ fontSize: "12px" }}>{translationSummary}</span>}
                </Flex>
                <Flex gap={8} align="center" justify="flex-end">
                    {searchVisible && (
                        <Input
                            placeholder={t('Search...')}
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                        />
                    )}
                    <Button size="large" onClick={toggleSearch}>
                        <SearchOutlined />
                    </Button>
                    <div className="upload-btn">
                        <Button size="large">
                            <input type="file" onChange={(event) => customRequest(event)} />
                            <UploadOutlined style={{ marginRight: "5px" }} />
                        </Button>

                    </div>
                    <Dropdown
                        trigger="click"
                        menu={{ items: databaseActions }}
                        placement="bottomRight"
                    >
                        <Button size="large"><MoreOutlined /></Button>
                    </Dropdown>
                    <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleNewDataAddition}>{t('New')}</Button>
                </Flex>
            </Flex>
            <div className="table-content">
                {
                    formFields.length > 0 ?
                        <Table scroll={{ x: 'max-content' }} className="white-header-table" locale={locale} columns={columns} dataSource={filteredData} pagination={{ position: ["bottomLeft"] }} />

                        : <Flex align="center" justify="center" style={{ border: "1px solid #E8E8FA", borderRadius: "1rem", padding: "1.5rem" }}>
                            <Flex vertical align="center" justify="center" style={{ width: "350px", textAlign: "center" }}>
                                <img src="/assets/MasterData.svg" alt="master-data-table-icon" width="180px" />
                                <p className="text-color-primary" style={{ fontSize: "1rem", fontWeight: "600", color: "#000" }}>{t('Add new entry')}</p>
                                <p className="text-color-secondary" style={{ fontSize: "0.875rem", fontWeight: "400" }}>{t('Create a form to specify inputs of the master data and then click')} <b>"+ New"</b> {t('to add an entry in database')}</p>
                                <Button size="large" className="submit-button mt-4" onClick={gotoForm}>{t('Create Form')}</Button>

                            </Flex>
                        </Flex>
                }
            </div>

            <Modal
                className="add-data-modal"
                title={displayTableName}
                open={openDataForm}
                onOk={closeDataFormModal}
                onCancel={closeDataFormModal}
                footer={null}
                destroyOnHidden={true}
            >
                {!isModalTranslating && <Button className="edit-form-btn" icon={<EditOutlined />} onClick={gotoForm}>{t('Edit Form')}</Button>}
                {/* Translating needs a canonical record to fall back to, so the
                    selector only appears once the record exists */}
                {isEdit &&
                    <Flex vertical gap={4} style={{ marginBottom: "16px" }}>
                        <Select
                            style={{ width: "100%" }}
                            value={modalLang}
                            onChange={handleModalLanguageChange}
                            options={FORM_LANGUAGE_OPTIONS}
                        />
                        {isModalTranslating &&
                            <span className="text-color-secondary" style={{ fontSize: "12px" }}>
                                {t("Editing translation — only text columns are saved for this language. Every other value is shared across languages.")}
                            </span>
                        }
                    </Flex>
                }
                <div className="add-master-data-form">
                    <Form
                        form={form}
                        onFinish={saveData}
                        layout="vertical"
                        preserve={false}
                    >
                        <AddDataModal form={form} handleItemChange={handleItemChange} formItems={formFields} masterList={combinedMasterList} formValues={formValues} masterData={masterData} isEdit={isEdit} translationMode={isModalTranslating} translationLanguage={modalLang} canonicalRecord={selectedRawRecord} />
                        <Flex gap={16} justify="flex-end" style={{ marginTop: "1.5rem" }}>
                            <Button onClick={closeDataFormModal}>{t('Cancel')}</Button>
                            <Button htmlType="submit" className="submit-button">{t('Save')}</Button>
                        </Flex>
                    </Form>
                </div>
            </Modal>
        </div>
    )
}

export default DataTables
